import { defineStore } from "pinia";

import type {
  AuthResponse,
  AuthTokenCreationQuery,
  RecoverPasswordCreationQuery,
  ResetPasswordCreationQuery,
} from "@/Modules/Models/Domain/Auth";
import type { AuthState } from "./Models/State";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";

import { LocalStorageService } from "@/Services/LocalStorageService";
import { isValidString } from "@/Utils/String";

import verifyTokenAH from "./ActionHandlers/VerifyToken";
import recoverPasswordAH from "./ActionHandlers/RecoverPassword";
import logoutAH from "./ActionHandlers/Logout";
import createUserAuthTokenAH from "./ActionHandlers/CreateUserAuthToken";
import resetPasswordAH from "./ActionHandlers/ResetPassword";
import setUserTokenAH from "./ActionHandlers/SetUserToken";
import clearUserTokenAH from "./ActionHandlers/ClearUserToken";
import refreshTokenAH from "./ActionHandlers/RefreshToken";
import regenerate2FCode from "./ActionHandlers/Regenerate2FCode";
import send2FactorCode, { type Send2FactorCodeProps } from "./ActionHandlers/Send2FCode";

const LocalStorage = new LocalStorageService(LocalStorageNamespace.AUTH);

const authState: AuthState = {
  isLoading: false,
  notification: null,
  userToken: LocalStorage.get(LocalStorageKey.USER_TOKEN),
};

export const useAuthStore = defineStore(StateNamespace.AUTH, {
  state: () => ({ ...authState }),
  getters: {
    isUserLogged(state: AuthState): boolean {
      return isValidString(state.userToken);
    },
  },
  actions: {
    createUserAuthToken(authTokenCreationQuery: AuthTokenCreationQuery): Promise<AuthResponse | undefined> {
      return createUserAuthTokenAH(authTokenCreationQuery);
    },

    refresh2FCode(currentHashCode: string): Promise<{ hash2F: string } | undefined> {
      return regenerate2FCode(currentHashCode);
    },

    send2FactorCode(props: Send2FactorCodeProps): Promise<AuthResponse | undefined> {
      return send2FactorCode(props);
    },

    hideNotification() {
      this.notification = null;
    },

    setUserToken(token: string) {
      setUserTokenAH(token);
    },

    clearUserToken() {
      clearUserTokenAH();
    },

    logout() {
      logoutAH();
    },

    async recoverPassword(recoverPasswordCreationQuery: RecoverPasswordCreationQuery) {
      this.isLoading = true;

      await recoverPasswordAH(recoverPasswordCreationQuery);
    },

    async resetPassword(resetPasswordCreationQuery: ResetPasswordCreationQuery) {
      this.isLoading = true;

      await resetPasswordAH(resetPasswordCreationQuery);
    },

    verifyToken(token: string) {
      verifyTokenAH(token);
    },

    refreshToken(): Promise<boolean> {
      return refreshTokenAH();
    },
  },
});

export type AuthStore = ReturnType<typeof useAuthStore>;

export default useAuthStore;
